"use client";

import type { SpecimenMarqueeComponent } from "../../types";
import { RuntimeImage } from "../../runtime/RendererRuntime";
import { resolveImageValue } from "../../utils/imageOptions";

/**
 * Bande défilante de spécimens, demandée par le cahier de l'Hôtel des Monnaies :
 * la vitrine des documents de sécurité, en mouvement lent et continu, plutôt
 * qu'une grille figée de plus.
 *
 * Trois exigences du cahier tiennent la mise en oeuvre :
 *   - le défilement s'arrête au SURVOL et au FOCUS clavier, pour qu'on puisse
 *     lire un libellé sans courir après lui ;
 *   - sur téléphone la bande reste tirable au doigt, d'où le conteneur
 *     `overflow-x-auto` qui laisse le défilement natif faire son travail ;
 *   - chaque visuel porte la mention « Spécimen » quand elle est requise, pour
 *     qu'aucun fac-similé ne puisse passer pour un document authentique.
 *
 * L'animation vit dans `globals.css` (`.bcc-specimen-track`) et non en classe
 * utilitaire : Tailwind ne voit pas les classes à valeur libre écrites dans le
 * JS compilé du SDK, le bandeau des taux a déjà rencontré ce mur.
 * `prefers-reduced-motion` coupe l'animation, la bande reste alors lisible et
 * simplement défilable à la main.
 */
export function SpecimenMarqueeRenderer({ component }: { component: SpecimenMarqueeComponent }) {
  const { title, items = [], specimenLabel = "Spécimen", ariaLabel } = component.content ?? {};
  const visibles = items.filter((i) => resolveImageValue(i.src).src);
  if (!visibles.length) return null;

  // Piste dupliquée : la boucle se referme sans saut visible. La copie est
  // masquée aux lecteurs d'écran, qui liraient sinon deux fois la même liste.
  const piste = [
    ...visibles.map((item, idx) => ({ item, idx, copie: false })),
    ...visibles.map((item, idx) => ({ item, idx, copie: true })),
  ];

  return (
    <section aria-label={ariaLabel ?? title ?? "Spécimens de documents de sécurité"}>
      {title ? (
        <h2 className="text-lg font-semibold text-[#0F2849] sm:text-xl">{title}</h2>
      ) : null}
      <div
        className={`bcc-specimen-marquee overflow-x-auto ${title ? "mt-4 sm:mt-5" : ""}`}
        tabIndex={0}
      >
        <ul className="bcc-specimen-track flex w-max gap-4 py-1 sm:gap-5">
          {piste.map(({ item, idx, copie }) => {
            const { src } = resolveImageValue(item.src);
            return (
              <li
                key={`${idx}-${copie ? "copie" : "original"}`}
                aria-hidden={copie ? true : undefined}
                className="w-44 shrink-0 sm:w-56"
              >
                {/* `object-contain` et non `object-cover` : ces visuels sont des documents,
                    pas des photographies. Un recadrage en couperait le cadre de sécurité et
                    la pastille imprimée, précisément ce qu'il s'agit de montrer. */}
                <div className="relative overflow-hidden rounded-lg border border-bcc-panel bg-[#F4F8FC] shadow-bcc-panel">
                  <RuntimeImage
                    src={src as string}
                    alt={copie ? "" : item.caption || ""}
                    className="h-32 w-full object-contain p-2 sm:h-40"
                  />
                  {item.specimen === false ? null : (
                    <span className="absolute left-2 top-2 rounded bg-[#0F2849]/80 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
                      {specimenLabel}
                    </span>
                  )}
                </div>
                {item.caption ? (
                  <p className="mt-2 line-clamp-2 text-[13px] leading-snug text-[#5D6E8C]">
                    {item.caption}
                  </p>
                ) : null}
              </li>
            );
          })}
        </ul>
      </div>
    </section>
  );
}
